import React from 'react';
import GLOBALS from '../config/globals';

const FAQPage = () => {
    return (
        <div className="container">
            <h1 className="header">FAQ</h1>

            <h2>The Basics</h2>

            <h3>How does the game work?</h3>
            <div className="paragraph">
                Uranium Baseball is a dice-based baseball card game. Each player card has outcome columns for dice rolls 2–12. Every at-bat, two dice are rolled to determine the result — a hit, walk, strikeout, or out — based on the batter's or pitcher's card. Collect cards, build a team of 24 players, and compete against AI opponents or other users.
            </div>

            <h3>Card Rarities</h3>
            <div className="paragraph">
                Cards come in six rarity tiers based on their rating:
                <ul>
                    <li><b>Iron</b> — rating up to 55 (most common)</li>
                    <li><b>Bronze</b> — rating up to 65</li>
                    <li><b>Silver</b> — rating up to 75</li>
                    <li><b>Gold</b> — rating up to 85</li>
                    <li><b>Titanium</b> — rating up to 95</li>
                    <li><b>Uranium</b> — no cap (rarest, best cards)</li>
                </ul>
            </div>

            <h3>Stamina &amp; Fatigue</h3>
            <div className="paragraph">
                Players build up fatigue as they play games. Fatigue resets each calendar day (UTC).
                <ul>
                    <li><b>Pitchers</b> accumulate fatigue based on the number of batters they face. When their fatigue reaches their stamina rating, they're exhausted and can't pitch until the next day. You'll see a stamina bar on their card during games — when it hits zero, their performance drops.</li>
                    <li><b>Batters</b> have a fatigue threshold of 18 (roughly 2 full games). After playing enough innings, they need to rest until the next day.</li>
                    <li>If a player is too tired before a game starts, you'll be prompted to substitute them with a fresh player from your inventory.</li>
                </ul>
            </div>

            <h2>Tournaments</h2>

            <h3>How do tournaments work?</h3>
            <div className="paragraph">
                A new daily tournament runs every day. Your best team is automatically entered.
                <ul>
                    <li>Challenge other teams on the leaderboard to play games. You&apos;re randomly assigned home or away.</li>
                    <li>Winning earns ELO rating points; losing costs them.</li>
                    <li>At the end of the day, the tournament finalizes — players are ranked by ELO and rewards are distributed.</li>
                    <li>Top finishers earn packs, Tournament Packs (TP), and money. The MVP (1st place) gets the best rewards.</li>
                    <li>Teams that didn&apos;t play any games are ranked at the bottom and receive no rewards.</li>
                    <li>All tournament games must be completed before the timer runs out — any unfinished games won&apos;t count.</li>
                    <li>You can view past tournament results from the History tab on the tournament page.</li>
                </ul>
            </div>

            <h3>Tournament Leagues</h3>
            <div className="paragraph">
                Tournaments are divided into six leagues, modeled after the minor league baseball system:
                <ul>
                    <li><b>Low-A</b> → <b>Single-A</b> → <b>High-A</b> → <b>AA</b> → <b>AAA</b> → <b>The Show</b></li>
                </ul>
                Everyone starts in Low-A. Each league runs its own daily tournament — you only compete against teams in your league.
                <ul>
                    <li><b>Promotion:</b> Win the tournament (finish 1st overall) and you move up to the next league.</li>
                    <li><b>Relegation:</b> Finish last overall and you drop down a league.</li>
                    <li>Promotion and relegation only activate once a league has enough players competing.</li>
                    <li>You can browse other leagues from the tabs at the top of the tournament page, but you can only play in your own.</li>
                    <li>Trophies reflect your league — higher leagues earn more prestigious trophies with special visual effects.</li>
                </ul>
            </div>

            <h3>Tournament Stamina</h3>
            <div className="paragraph">
                Tournaments track fatigue <b>separately</b> from regular games. Playing a tournament game does not tire your players for regular games, and vice versa.
                <ul>
                    <li>Tournament fatigue uses the same rules — pitchers have stamina limits, batters have an innings threshold.</li>
                    <li>Tournament fatigue resets each calendar day, just like regular fatigue.</li>
                    <li>If a player is fatigued for a tournament game, you can substitute them before starting the match.</li>
                </ul>
            </div>

            <h2>Advanced Mechanics</h2>

            <h3>How do pitcher cards affect at-bats?</h3>
            <div className="paragraph">
                Every at-bat, three dice are rolled — one for the batter's outcome, one for extra bases, and one for the pitcher's card. The pitcher's roll looks up a modifier on their card (like hits, walks, strikeouts, or power) and applies it to the batter's result.
                <br /><br />
                Each pitcher dice result is a modifier that changes the batter's outcome. The modifier can go up (▲, favorable for pitcher) or down (▼, unfavorable):
                <ul>
                    <li><b>Strikeouts (▲)</b> — outs and walks can become strikeouts. The pitcher is overpowering the batter.</li>
                    <li><b>Strikeouts (▼)</b> — strikeouts become a re-roll on the batter's card. The pitcher can't finish hitters off.</li>
                    <li><b>Hits (▲)</b> — outs can become singles. The pitcher is hittable.</li>
                    <li><b>Hits (▼)</b> — singles become outs. Doubles and home runs can also get knocked down a level depending on how good the pitcher is. The pitcher is suppressing hard contact.</li>
                    <li><b>Walks (▲)</b> — strikeouts can become walks. The pitcher is missing the zone.</li>
                    <li><b>Walks (▼)</b> — walks become outs. The pitcher has pinpoint control.</li>
                    <li><b>Power (▲)</b> — doubles can become home runs, singles can become doubles. The pitcher is giving up big hits.</li>
                    <li><b>Power (▼)</b> — home runs become doubles, doubles become singles, singles can become outs. The pitcher keeps the ball in the park.</li>
                </ul>
                Higher-rated pitchers have more favorable (▲ Strikeouts, ▼ Hits, ▼ Walks, ▼ Power) modifiers across their dice columns.
            </div>

            <h3>How does pitcher fatigue work in-game?</h3>
            <div className="paragraph">
                As a pitcher faces more batters, they build up fatigue. Once they've faced more batters than their stamina allows, their performance starts to degrade — and it gets worse the longer you leave them in.
                <ul>
                    <li>Each batter faced adds to the pitcher's workload. Once they pass their stamina threshold, fatigue kicks in.</li>
                    <li>When fatigued, the pitcher's favorable modifiers start flipping against them. A result that would have helped the pitcher (like suppressing a hit or getting a strikeout) can suddenly help the batter instead.</li>
                    <li>The more overworked the pitcher, the more likely every good modifier flips. A pitcher who's a few batters past their limit might get away with it sometimes, but one who's deep into exhaustion will have nearly every modifier working against them.</li>
                    <li>A fully gassed pitcher is worse than having no pitcher modifier at all — fatigue can even create negative modifiers on rolls where the pitcher's card had no effect normally.</li>
                    <li>This is why pulling tired pitchers matters — a reliever with fresh legs keeps their good modifiers intact, while a gassed starter is actively hurting your team on every pitch.</li>
                </ul>
            </div>

            <h3>Stealing Bases</h3>
            <div className="paragraph">
                Runners can attempt to steal bases during a game. A runner's speed affects their chance of success — but beware: the more you steal, the harder it gets. The defense adjusts to aggressive baserunning, so pick your moments wisely.
            </div>

            <h2>Card Upgrades</h2>

            <h3>How do card upgrades work?</h3>
            <div className="paragraph">
                Every card earns EXP from gameplay — batters earn it from plate appearances, hits, RBIs, and runs scored; pitchers earn it from outs and strikeouts. Once a card has enough EXP and money, you can spend both to upgrade that card. Each upgrade level lets you invest one point into a stat of your choice.
                <ul>
                    <li>Cards can reach up to <b>level 10</b>. Each level costs more EXP and money than the last. The money cost is half the EXP cost.</li>
                    <li>Upgrades are <b>permanent</b> — choose wisely.</li>
                    <li>To upgrade, go to a card's detail page and click the EXP line or the <b>LEVEL UP</b> button when you have enough EXP and money.</li>
                    <li>Upgraded cards show a green level badge on the card itself.</li>
                </ul>
            </div>

            <h3>Common Upgrades (Batters &amp; Pitchers)</h3>
            <div className="paragraph">
                <ul>
                    <li><b>Super Star</b> — +$5 per game per point. Fans come to see the star play.</li>
                    <li><b>Drug Dealer</b> — +5% chance per point (max 50%) to score a bonus energy drink at game end. Each card with this perk rolls independently.</li>
                </ul>
            </div>

            <h3>Batter Upgrades</h3>
            <div className="paragraph">
                <ul>
                    <li><b>Stamina</b> — +1 extra game per day per point. At max, a batter can play 12 games in a day instead of 2.</li>
                    <li><b>Speed</b> — +3 speed per point, improving stolen base and extra-base advancement chances.</li>
                    <li><b>Power</b> — +5% reroll chance per point (max 50%) when a double is rolled on the extra-base table. The reroll can only upgrade the result — never downgrade.</li>
                </ul>
            </div>

            <h3>Pitcher Upgrades</h3>
            <div className="paragraph">
                <ul>
                    <li><b>Stamina</b> — +3 batters per point (~1 extra inning). Delays fatigue from kicking in.</li>
                    <li><b>XBH Suppress</b> — +2 effective rating per point when the pitcher's dice trigger a favorable hit modifier. Better chance of knocking down extra-base hits.</li>
                    <li><b>Single Suppress</b> — +2 effective rating per point when the pitcher's dice trigger a favorable power modifier. Better chance of suppressing singles.</li>
                    <li><b>K Modifier</b> — +2.5% conversion rate per point (up to 75%) when the pitcher's strikeout dice fire. Walks and outs become strikeouts more often.</li>
                    <li><b>Fatigue Fighter</b> — -3% fatigue penalty per point. Reduces the chance of fatigue flipping a good pitcher result into a bad one. Lets your pitcher stay effective deeper into games.</li>
                </ul>
                With 10 upgrade points across multiple stats, pitchers have to specialize — you can't max everything.
            </div>

            <h3>Variations and Upgrades</h3>
            <div className="paragraph">
                Each card variation (Chrome, Refractor, etc.) has its own upgrade track. If you have a base copy and a Chrome copy of the same card, they level up independently. When building a team, each variation appears as a separate option in the dropdown — pick the one with the upgrades you want.
            </div>

            <h2>Other</h2>

            <h3>Bug Reporting</h3>
            <div className="paragraph">
                Just tell Dan at the{' '}
                <a href={GLOBALS.DISCORD_INVITE_URL} target="_blank" rel="noopener noreferrer">
                    discord server
                </a>{' '}
                for now.
            </div>

            <h3>Known Issues</h3>
            <div className="paragraph">
                <ul>
                    <li>Subs are limited for now — you can only sub for a player that plays the same position.</li>
                </ul>
            </div>
        </div>
    );
};

export default FAQPage;
